import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import MagazzinoDetailDialog from "@/components/MagazzinoDetailDialog";
import { toast } from "sonner";
import { Loader2, CalendarRange, PackageX, TrendingUp, Download, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

const cellClass = (m) => {
  if (m.scoperto > 0) return "bg-red-500/15 text-red-500 font-semibold";
  if (m.saldo_fine > 0) return "text-emerald-600 dark:text-emerald-400";
  return "text-muted-foreground";
};

export default function Approvvigionamenti() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [storicoBusy, setStoricoBusy] = useState(false);
  const [storicoProg, setStoricoProg] = useState(null);
  const [q, setQ] = useState("");
  const [soloMov, setSoloMov] = useState(false);
  const [detCod, setDetCod] = useState(null);
  // controlli
  const [mesi, setMesi] = useState(6);
  const [forecast, setForecast] = useState(false);
  const [incremento, setIncremento] = useState(0);
  const [baseMesi, setBaseMesi] = useState(6);
  const [excluded, setExcluded] = useState(new Set());
  const [pdfBusy, setPdfBusy] = useState(false);

  const toggleCat = (cat) => setExcluded((prev) => { const n = new Set(prev); n.has(cat) ? n.delete(cat) : n.add(cat); return n; });

  const reloadData = () => {
    const params = { mesi };
    if (forecast) { params.forecast = true; params.incremento = Number(incremento) || 0; params.base_mesi = baseMesi; }
    api.get("/magazzino/approvvigionamenti", { params }).then((r) => setData(r.data)).catch(() => {});
  };

  const avviaStorico = async () => {
    setStoricoBusy(true); setStoricoProg(null);
    try {
      await api.post("/magazzino/sync/storico");
      toast.info("Scarico movimenti degli ultimi 12 mesi avviato…");
      const poll = setInterval(async () => {
        try {
          const r = await api.get("/magazzino/sync/storico/status");
          setStoricoProg(r.data);
          if (!r.data.running) {
            clearInterval(poll);
            setStoricoBusy(false);
            if (r.data.error) toast.error("Storico: " + r.data.error);
            else toast.success(`Storico aggiornato: ${r.data.articoli} articoli · ${r.data.movimenti} movimenti`);
            reloadData();
          }
        } catch { clearInterval(poll); setStoricoBusy(false); }
      }, 2500);
    } catch (e) {
      setStoricoBusy(false);
      toast.error(e.response?.data?.detail || "Avvio storico non riuscito");
    }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const params = { mesi };
      if (forecast) { params.forecast = true; params.incremento = Number(incremento) || 0; params.base_mesi = baseMesi; }
      if (excluded.size) params.escludi = [...excluded].join("~~");
      const r = await api.get("/magazzino/approvvigionamenti/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a");
      a.href = url; a.download = "pianificazione-torri.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download PDF non riuscito"); } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    setData(null);
    const t = setTimeout(() => {
      const params = { mesi };
      if (forecast) { params.forecast = true; params.incremento = Number(incremento) || 0; params.base_mesi = baseMesi; }
      api.get("/magazzino/approvvigionamenti", { params }).then((r) => setData(r.data)).catch(() => setData(false));
    }, 350);
    return () => clearTimeout(t);
  }, [mesi, forecast, incremento, baseMesi]);

  const mesiHdr = data && data.mesi ? data.mesi : [];

  const categorie = useMemo(() => {
    if (!data || !data.categorie) return [];
    const term = q.trim().toLowerCase();
    return data.categorie.map((c) => {
      let arts = c.articoli;
      if (soloMov) arts = arts.filter((a) => a.ha_movimenti || a.giacenza !== 0 || (a.forecast_mensile || 0) > 0);
      if (term) arts = arts.filter((a) => a.codice.toLowerCase().includes(term) || (a.descrizione || "").toLowerCase().includes(term));
      return { ...c, _arts: arts };
    }).filter((c) => c._arts.length > 0);
  }, [data, q, soloMov]);

  const controls = (
    <Card className="p-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-muted-foreground">Orizzonte:</span>
          <div className="flex overflow-hidden rounded-lg border border-border">
            <Button variant={mesi === 6 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setMesi(6)} data-testid="appro-horizon-6">6 mesi</Button>
            <Button variant={mesi === 12 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setMesi(12)} data-testid="appro-horizon-12">1 anno</Button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="forecast" checked={forecast} onCheckedChange={setForecast} data-testid="appro-forecast-toggle" />
          <Label htmlFor="forecast" className="flex items-center gap-1.5 text-sm"><TrendingUp className="h-4 w-4 text-amber-500" /> Includi forecast vendite</Label>
        </div>
        {forecast && (
          <>
            <div className="flex items-center gap-2">
              <Label htmlFor="incremento" className="text-xs font-medium text-muted-foreground">Incremento %</Label>
              <Input id="incremento" type="number" value={incremento} onChange={(e) => setIncremento(e.target.value)} className="h-9 w-24" data-testid="appro-incremento" />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-muted-foreground">Media su:</span>
              <div className="flex overflow-hidden rounded-lg border border-border">
                <Button variant={baseMesi === 6 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setBaseMesi(6)} data-testid="appro-base-6">6 mesi</Button>
                <Button variant={baseMesi === 12 ? "default" : "ghost"} size="sm" className="rounded-none" onClick={() => setBaseMesi(12)} data-testid="appro-base-12">12 mesi</Button>
              </div>
            </div>
          </>
        )}
        <div className="flex flex-1 items-center gap-4">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca articolo o codice…" className="h-9 max-w-xs" data-testid="appro-search" />
          <div className="flex items-center gap-2">
            <Switch id="solo-mov" checked={soloMov} onCheckedChange={setSoloMov} data-testid="appro-solo-mov" />
            <Label htmlFor="solo-mov" className="text-xs text-muted-foreground">Solo con movimenti</Label>
          </div>
        </div>
      </div>
      {forecast && (
        <p className="mt-3 text-[11px] text-amber-600 dark:text-amber-400" data-testid="appro-forecast-note">
          Forecast attivo (nettato sugli ordini reali): nei mesi con uscite reali il forecast è parziale o nullo — l'uscita del mese è il <span className="font-medium">massimo tra reale e forecast</span>. La quota di forecast effettivamente applicata è in <span className="font-medium">ambra</span> (~) e incide su saldo e scoperti. Il forecast è la media dei <span className="font-medium">movimenti in uscita</span> degli ultimi {baseMesi} mesi{Number(incremento) ? ` maggiorata del ${incremento}%` : ""}.
        </p>
      )}
    </Card>
  );

  if (data === null) return (
    <div className="space-y-6" data-testid="approvvigionamenti-page">
      <Header count={mesiHdr.length} forecast={forecast} />
      {controls}
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground" data-testid="appro-loading"><Loader2 className="h-4 w-4 animate-spin" /> Carico il piano approvvigionamenti…</div>
    </div>
  );
  if (data === false) return (
    <div className="space-y-6" data-testid="approvvigionamenti-page">
      <Header count={mesiHdr.length} forecast={forecast} />
      {controls}
      <div className="p-6 text-sm text-red-500" data-testid="appro-error">Errore nel recupero dei dati.</div>
    </div>
  );

  const totScoperti = data.categorie.filter((c) => !excluded.has(c.categoria)).reduce((s, c) => s + c.n_scoperti, 0);

  return (
    <div className="space-y-6" data-testid="approvvigionamenti-page">
      <Header count={mesiHdr.length} forecast={data.forecast} />
      {controls}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy} data-testid="appro-pdf-btn">
            {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} PDF per team pianificazione
          </Button>
          {data.forecast && (
            <Badge variant="outline" data-testid="appro-storico-badge" className={data.storico?.attivo ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : "border-amber-500/50 text-amber-500"}>
              {data.storico?.attivo ? `Storico consumi: ${data.storico.mesi} mesi · agg. ${fmtDate(data.storico.aggiornato_al)}` : "Storico non ancora popolato · stima su impegni"}
            </Badge>
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={avviaStorico} disabled={storicoBusy}
              title="Scarica dal gestionale i movimenti in uscita degli ultimi 12 mesi (alimenta il forecast). Non tocca giacenza/impegni."
              data-testid="appro-storico-sync-btn">
              {storicoBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <History className="mr-2 h-4 w-4" />}
              {storicoBusy
                ? (storicoProg && storicoProg.total ? `Storico ${storicoProg.done}/${storicoProg.total}…` : "Scarico storico…")
                : "Aggiorna storico 12 mesi"}
            </Button>
          )}
        </div>
        <Badge variant="outline" className="border-red-500/50 text-red-500" data-testid="appro-tot-scoperti">{totScoperti} articoli in scoperto</Badge>
      </div>

      {categorie.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="appro-empty"><PackageX className="mx-auto mb-2 h-6 w-6" /> Nessun articolo corrisponde ai filtri.</Card>
      ) : (
        <Accordion type="multiple" className="space-y-3">
          {categorie.map((c) => { const isExcl = excluded.has(c.categoria); return (
            <AccordionItem key={c.categoria} value={c.categoria} className={`rounded-xl border border-border bg-card px-4 ${isExcl ? "opacity-50" : ""}`} data-testid={`appro-cat-${c.categoria}`}>
              <div className="flex items-center gap-2">
                <Checkbox checked={!isExcl} onCheckedChange={() => toggleCat(c.categoria)} title={isExcl ? "Includi categoria" : "Escludi categoria"} data-testid={`appro-cat-toggle-${c.categoria}`} />
                <AccordionTrigger className="flex-1 hover:no-underline">
                  <div className="flex flex-1 flex-wrap items-center gap-2 pr-3">
                    <span className="font-heading text-base font-bold">{c.categoria}</span>
                    <Badge variant="secondary">{c._arts.length} art.</Badge>
                    {c.n_scoperti > 0 && <Badge variant="outline" className="border-red-500/50 text-red-500">{c.n_scoperti} scoperti</Badge>}
                  </div>
                </AccordionTrigger>
              </div>
              {!isExcl && (<AccordionContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-xs">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="sticky left-0 bg-card py-2 pr-3 text-left font-medium">Articolo</th>
                        <th className="px-2 py-2 text-right font-medium">Giac.</th>
                        {mesiHdr.map((m) => <th key={m.label} className="px-2 py-2 text-right font-medium capitalize">{m.label_it}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {c._arts.map((a) => (
                        <tr key={a.codice} className="border-t border-border/50" data-testid={`appro-art-${a.codice}`}>
                          <td className="sticky left-0 bg-card py-2 pr-3">
                            <button type="button" onClick={() => setDetCod(a.codice)} className="font-mono text-xs font-bold underline decoration-dotted underline-offset-2 hover:text-primary" title="Vedi ingressi/uscite" data-testid={`appro-art-btn-${a.codice}`}>{a.codice}</button>
                            <div className="max-w-[240px] truncate text-[11px] text-muted-foreground">{a.descrizione}</div>
                            {a.arrivi?.length > 0 && (
                              <div className="mt-1 space-y-0.5 text-[10px] leading-tight text-emerald-600 dark:text-emerald-400" data-testid={`appro-arrivi-${a.codice}`}>
                                {a.arrivi.map((x, k) => (
                                  <div key={k} className="max-w-[240px] truncate" title={`${x.fornitore || ""} · ${x.ordine || ""}`}>
                                    Arrivo {new Date(x.data).toLocaleDateString("it-IT")} · +{x.qta}{x.ordine ? ` · ${x.ordine}` : ""}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-right font-medium">{a.giacenza}</td>
                          {a.mesi.map((m, i) => (
                            <td key={i} className={`px-2 py-2 text-right ${cellClass(m)}`} title={`Ingressi +${m.ingressi} · Uscite -${m.uscite}${m.forecast ? ` · Forecast -${m.forecast}` : ""}`}>
                              {m.saldo_fine}
                              {(m.ingressi > 0 || m.uscite > 0 || m.forecast > 0) && (
                                <div className="text-[9px] font-normal leading-tight">
                                  <span className="text-emerald-600 dark:text-emerald-400">+{m.ingressi}</span>
                                  <span className="text-muted-foreground">/</span>
                                  <span className="text-red-500">-{m.uscite}</span>
                                  {m.forecast > 0 && <span className="text-amber-500"> ~-{m.forecast}</span>}
                                </div>
                              )}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </AccordionContent>)}
            </AccordionItem>
          ); })}
        </Accordion>
      )}

      <MagazzinoDetailDialog open={!!detCod} onOpenChange={(o) => { if (!o) setDetCod(null); }} codice={detCod} />
    </div>
  );
}

function Header({ count, forecast }) {
  return (
    <div>
      <h1 className="flex items-center gap-2 font-heading text-2xl font-bold"><CalendarRange className="h-6 w-6 text-primary" /> Pianificazione</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Disponibilità residua dei prossimi {count || 6} mesi per ogni articolo a listino, calcolata dai movimenti di magazzino (ingressi − uscite{forecast ? " − forecast vendite" : ""}). In <span className="font-medium text-red-500">rosso</span> i mesi in scoperto. Clicca il codice per il dettaglio movimenti.
      </p>
    </div>
  );
}
